import _, { ListIteratee, escapeRegExp } from "lodash";
import { SortDirection } from "react-virtualized";

import { EventType, eventDefType } from "../eventTypes";
import { getDestinationsForEvent } from "./utils";
import { LoadedEventDefinition, LoadedDestinationInfo } from "../types/global";
import { DestinationDetail } from "./types";

export type FilterSettings = {
  searchQuery: string;
  destinationSettings: Array<string>;
  typeSettings: Array<EventType>;
  tagSettings: Array<string>;
  showHidden: boolean;
};

////////////////////////////////////////////////////////
// Utilities
////////////////////////////////////////////////////////
const noFilter = (event: LoadedEventDefinition) => {
  return true;
};

////////////////////////////////////////////////////////
// Filters
////////////////////////////////////////////////////////
const buildSearchQueryFilter = (searchQuery: string) => {
  const escaped = escapeRegExp(searchQuery);
  const regex = new RegExp(escaped, "gi");
  return (event: LoadedEventDefinition) => {
    return [event.description, event.name].some((text) => regex.test(text));
  };
};

const buildTagFilter = (tagSettings: Array<string>) => {
  return (event: LoadedEventDefinition) =>
    tagSettings.some((tag) => !!event.tags[tag]);
};

const buildTypeFilter = (typeSettings: Array<EventType>) => {
  return (event: LoadedEventDefinition) =>
    typeSettings.some((t) => eventDefType(event) === t);
};

const buildDestinationFilter = (
  destinations: Array<LoadedDestinationInfo>,
  configurableDestinations,
  destinationSettings: Array<string>
) => {
  return (event: LoadedEventDefinition) => {
    const destsForEvent = getDestinationsForEvent(
      destinations,
      configurableDestinations,
      event
    );
    return destinationSettings.some((destinationName: string) =>
      destsForEvent.some((de: DestinationDetail) => destinationName === de.id)
    );
  };
};

////////////////////////////////////////////////////////
// Public API
////////////////////////////////////////////////////////
const filterEvents = (
  eventDefs: Array<LoadedEventDefinition>,
  destinations: Array<LoadedDestinationInfo>,
  configurableDestinations,
  filterSettings: FilterSettings
) => {
  const searchQueryFilter = filterSettings.searchQuery
    ? buildSearchQueryFilter(filterSettings.searchQuery)
    : noFilter;

  const destinationFilter =
    filterSettings.destinationSettings.length > 0
      ? buildDestinationFilter(
          destinations,
          configurableDestinations,
          filterSettings.destinationSettings
        )
      : noFilter;

  const tagFilter =
    filterSettings.tagSettings.length > 0
      ? buildTagFilter(filterSettings.tagSettings)
      : noFilter;

  const typeFilter =
    filterSettings.typeSettings.length > 0
      ? buildTypeFilter(filterSettings.typeSettings)
      : noFilter;

  const hiddenFilter = (event: LoadedEventDefinition) =>
    !event.is_hidden || filterSettings.showHidden;

  return eventDefs
    .filter(searchQueryFilter)
    .filter(typeFilter)
    .filter(destinationFilter)
    .filter(tagFilter)
    .filter(hiddenFilter);
};

const sortEvents = (
  events: Array<LoadedEventDefinition>,
  sortBy,
  sortDirection
) => {
  // Values with a null updated_at should be put last in the list
  const defaultSorter = (event: LoadedEventDefinition) => {
    return event.updated_at || Infinity;
  };

  let sortFieldFn: ListIteratee<LoadedEventDefinition>;

  if (sortBy === "type") {
    sortFieldFn = (event: LoadedEventDefinition) => eventDefType(event);
  } else if (sortBy) {
    sortFieldFn = sortBy;
  } else {
    sortFieldFn = defaultSorter;
  }

  const sortDir = sortDirection || "desc";

  return _.orderBy(
    events,
    [sortFieldFn],
    [sortDir === SortDirection.ASC ? "asc" : "desc"]
  );
};

export { filterEvents, sortEvents };
